import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const slugify = (s) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  category: "Innovazione",
  author: "Aéràs Technology",
  read_minutes: 5,
  published: true,
  tags: [],
};

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tagsInput, setTagsInput] = useState("");

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm(EMPTY);
    setTagsInput("");
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p });
    setTagsInput((p.tags ?? []).join(", "));
  };
  const close = () => { setEditing(null); setForm(EMPTY); setTagsInput(""); };

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      read_minutes: Number(form.read_minutes) || 5,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/blog", payload);
        toast.success("Articolo creato.");
      } else {
        await api.put(`/admin/blog/${editing}`, payload);
        toast.success("Articolo aggiornato.");
      }
      close();
      load();
    } catch (err) {
      toast.error(apiError(err) ?? "Errore salvataggio.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'articolo?")) return;
    await api.delete(`/admin/blog/${id}`);
    toast.success("Articolo eliminato.");
    load();
  };

  return (
    <div className="p-8 md:p-12" data-testid="admin-blog">
      <div className="flex items-baseline justify-between mb-12">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#007A8A] mb-2">— BLOG</div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter">Articoli</h1>
        </div>
        <button
          onClick={openNew}
          className="btn-primary"
          data-testid="admin-blog-new"
        >
          <Plus size={14} /> Nuovo articolo
        </button>
      </div>

      <div className="border border-[#e4e4e7]">
        <table className="w-full text-sm font-body">
          <thead>
            <tr className="border-b border-[#e4e4e7] font-mono text-[10px] tracking-widest uppercase text-[#3f3f46]">
              <th className="text-left p-4">Titolo</th>
              <th className="text-left p-4 hidden md:table-cell">Categoria</th>
              <th className="text-left p-4 hidden lg:table-cell">Slug</th>
              <th className="text-left p-4">Stato</th>
              <th className="text-right p-4">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-b border-[#e4e4e7] hover:bg-[#f4f4f5]" data-testid={`admin-post-row-${p.slug}`}>
                <td className="p-4">
                  <div className="font-medium">{p.title}</div>
                  <div className="text-[#52525b] text-xs mt-1 hidden md:block">{p.excerpt.slice(0, 80)}…</div>
                </td>
                <td className="p-4 hidden md:table-cell text-[#3f3f46]">{p.category}</td>
                <td className="p-4 hidden lg:table-cell font-mono text-xs text-[#52525b]">{p.slug}</td>
                <td className="p-4">
                  {p.published ? (
                    <span className="font-mono text-[10px] tracking-widest text-[#007A8A]">● PUB</span>
                  ) : (
                    <span className="font-mono text-[10px] tracking-widest text-[#52525b]">○ BOZZA</span>
                  )}
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(p)} className="p-2 hover:text-[#007A8A] transition-colors" data-testid={`edit-post-${p.slug}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} className="p-2 hover:text-[#FF4D00] transition-colors" data-testid={`delete-post-${p.slug}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-[#52525b]">Nessun articolo.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto p-6" data-testid="admin-post-editor">
          <form
            onSubmit={save}
            className="w-full max-w-3xl bg-[#f4f4f5] border border-[#e4e4e7] my-12"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#e4e4e7]">
              <div className="font-display text-2xl">{editing === "new" ? "Nuovo articolo" : "Modifica articolo"}</div>
              <button type="button" onClick={close} aria-label="Chiudi"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-5">
              <Field label="Titolo" required>
                <input required value={form.title} onChange={(e) => { set("title")(e); if (editing === "new") setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }} className="input-admin" data-testid="edit-title" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Slug" required><input required value={form.slug} onChange={set("slug")} className="input-admin" data-testid="edit-slug" /></Field>
                <Field label="Categoria"><input value={form.category} onChange={set("category")} className="input-admin" data-testid="edit-category" /></Field>
                <Field label="Autore"><input value={form.author} onChange={set("author")} className="input-admin" data-testid="edit-author" /></Field>
                <Field label="Minuti lettura"><input type="number" min="1" value={form.read_minutes} onChange={set("read_minutes")} className="input-admin" data-testid="edit-read-minutes" /></Field>
              </div>
              <Field label="Cover URL"><input value={form.cover_image ?? ""} onChange={set("cover_image")} placeholder="https://…" className="input-admin" data-testid="edit-cover" /></Field>
              <Field label="Tag (separati da virgola)"><input value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className="input-admin" data-testid="edit-tags" /></Field>
              <Field label="Excerpt" required><textarea required rows={2} value={form.excerpt} onChange={set("excerpt")} className="input-admin resize-none" data-testid="edit-excerpt" /></Field>
              <Field label="Contenuto (markdown / testo)" required><textarea required rows={10} value={form.content} onChange={set("content")} className="input-admin resize-none font-mono text-xs" data-testid="edit-content" /></Field>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.published} onChange={set("published")} />
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase">Pubblicato</span>
              </label>
            </div>
            <div className="p-6 border-t border-[#e4e4e7] flex justify-end gap-3">
              <button type="button" onClick={close} className="btn-ghost">Annulla</button>
              <button type="submit" className="btn-primary" data-testid="edit-save">Salva</button>
            </div>
          </form>
        </div>
      )}

      <style>{`
        .input-admin {
          width: 100%;
          background: transparent;
          border: 1px solid #d4d4d8;
          padding: 0.7rem 0.9rem;
          color: #0a0a0a;
          outline: none;
          font-family: 'IBM Plex Sans', sans-serif;
          transition: border-color 0.2s;
        }
        .input-admin:focus { border-color: #007A8A; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3f3f46] mb-2 inline-block">
        {label} {required && <span className="text-[#FF4D00]">*</span>}
      </span>
      {children}
    </label>
  );
}
